import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Select } from '@/components/common/Select'
import { Textarea } from '@/components/common/Textarea'
import { patientApi } from '@/api/patientApi'
import { usePatientStore } from '@/store/patientStore'
import { GENDER_OPTIONS } from '@/lib/constants'
import { cleanPayload } from '@/lib/utils'

/**
 * The only place that calls PATCH /patients/:id/personal-info.
 * Only changed fields are sent — backend keeps everything else as-is.
 */
export function EditPersonalInfoModal({ open, onClose, patient }) {
  const upsert = usePatientStore((s) => s.upsert)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open && patient) {
      setForm({
        name: patient.name || '',
        dob: patient.dob || '',
        gender: patient.gender || '',
        mobile_number: patient.mobile_number || '',
        whatsapp_number: patient.whatsapp_number || '',
        address: patient.address || '',
        occupation: patient.occupation || '',
        referred_by: patient.referred_by || '',
      })
    }
  }, [open, patient])

  const set = (patch) => setForm((f) => ({ ...f, ...patch }))

  const save = async () => {
    if (!patient) return
    // Diff against original — only send changed fields
    const changes = {}
    Object.keys(form).forEach((k) => {
      const before = patient[k] ?? ''
      const after = form[k] ?? ''
      if (String(before) !== String(after)) changes[k] = after === '' ? null : after
    })

    if (Object.keys(changes).length === 0) {
      toast('No changes to save', { icon: '💡' })
      return
    }

    setSaving(true)
    try {
      const updated = await patientApi.updatePersonalInfo(patient.patient_id, cleanPayload(changes) || {})
      upsert(updated)
      toast.success('Personal information updated')
      onClose?.()
    } catch {
      // toast is handled in axios interceptor
    } finally {
      setSaving(false)
    }
  }

  if (!patient) return null

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Update personal information"
      description="Only the fields you change are sent. Medical history is never affected."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} loading={saving}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Full name"
          value={form.name || ''}
          onChange={(e) => set({ name: e.target.value })}
          className="sm:col-span-2"
        />
        <Input
          label="Date of birth"
          type="date"
          value={form.dob || ''}
          onChange={(e) => set({ dob: e.target.value })}
          max={new Date().toISOString().slice(0, 10)}
        />
        <Select
          label="Gender"
          value={form.gender || ''}
          onChange={(e) => set({ gender: e.target.value })}
          options={GENDER_OPTIONS}
          placeholder="Select gender"
        />
        <Input
          label="Mobile number"
          value={form.mobile_number || ''}
          onChange={(e) => set({ mobile_number: e.target.value })}
        />
        <Input
          label="WhatsApp number"
          value={form.whatsapp_number || ''}
          onChange={(e) => set({ whatsapp_number: e.target.value })}
        />
        <Input
          label="Occupation"
          value={form.occupation || ''}
          onChange={(e) => set({ occupation: e.target.value })}
        />
        <Input
          label="Referred by"
          value={form.referred_by || ''}
          onChange={(e) => set({ referred_by: e.target.value })}
        />
        <Textarea
          label="Address"
          value={form.address || ''}
          onChange={(e) => set({ address: e.target.value })}
          rows={2}
          className="sm:col-span-2"
        />
      </div>
    </Modal>
  )
}
